<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { usePremiumStore } from '@stores/premium'
import { useAnalytics } from '@composables/use-analytics'
import { useLibraryEntry } from '@composables/use-library-entry'
import { NAV_DESTINATIONS, PREMIUM_DESTINATION } from '@data/nav-destinations'
import { navSectionFor } from '@utils/nav-section'
import NavItem from './nav-item'
import NavWordmark from './nav-wordmark'

const SLIDE_MS = 400 // motion/slow — keep in step with the layout's grid transition

const props = defineProps({
    layout: {
        type: String,
        required: true,
        validator: value => ['expanded', 'rail', 'tablet'].includes(value),
    },
})

const emit = defineEmits(['toggle'])

const route = useRoute()
const premiumStore = usePremiumStore()
const analyticsService = useAnalytics()
const { onLibraryClick } = useLibraryEntry()

const header = ref(null)
const isSliding = ref(false)
let slideTimer = null

const isExpanded = computed(() => props.layout === 'expanded')
const isRail = computed(() => props.layout === 'rail')
const activeSection = computed(() => navSectionFor(route.name))
// Logged-out visitors are free listeners too (PRD rule 7).
const showPremium = computed(() => !premiumStore.isPremium)

function onItemClick(destination, event) {
    if (destination.id === 'library') onLibraryClick(event)
    if (destination.id === 'premium') analyticsService.premiumUpsellInitiated('sidebar')
}

// Only the desktop expanded ⇄ rail switch animates; the tablet rail never does.
watch(
    () => props.layout,
    (next, previous) => {
        // Collapse and Expand are different elements: hand keyboard focus across or it
        // falls to <body> with the removed control (WCAG 2.4.3). This pre-flush watch
        // still sees the old control in the DOM.
        const hadFocus = typeof document !== 'undefined' && header.value?.contains(document.activeElement)
        if (hadFocus && next !== 'tablet') nextTick(() => header.value?.querySelector('button')?.focus())

        if (next === 'tablet' || previous === 'tablet') return
        isSliding.value = true
        clearTimeout(slideTimer)
        slideTimer = setTimeout(() => {
            isSliding.value = false
        }, SLIDE_MS)
    }
)

onBeforeUnmount(() => clearTimeout(slideTimer))
</script>

<template>
    <nav
        :class="['nav-sidebar', `nav-sidebar-${layout}`, { 'nav-sidebar-sliding': isSliding }]"
        aria-label="Main"
    >
        <div
            ref="header"
            class="nav-sidebar-header"
        >
            <template v-if="isExpanded">
                <!-- Wrapped: a class on NavWordmark lands on its root and clobbers its own filter transition. -->
                <div class="nav-sidebar-brand">
                    <NavWordmark />
                </div>
                <BaseButton
                    class="nav-sidebar-control"
                    variant="tertiary"
                    size="m"
                    icon-only
                    icon="sidebar-collapse"
                    label="Collapse navigation"
                    aria-expanded="true"
                    @click="emit('toggle')"
                />
            </template>
            <button
                v-else-if="isRail"
                class="nav-sidebar-mark nav-sidebar-mark-control"
                type="button"
                aria-label="Expand navigation"
                aria-expanded="false"
                @click="emit('toggle')"
            >
                <BaseIcon
                    class="nav-sidebar-mark-glyph"
                    name="logo-frisky"
                    :size="24"
                />
                <BaseIcon
                    class="nav-sidebar-expand-glyph"
                    name="sidebar-expand"
                    :size="24"
                />
            </button>
            <router-link
                v-else
                :to="{ name: 'home' }"
                class="nav-sidebar-mark"
                aria-label="FRISKY home"
            >
                <BaseIcon
                    class="nav-sidebar-mark-glyph"
                    name="logo-frisky"
                    :size="24"
                />
            </router-link>
        </div>

        <div class="nav-sidebar-rows">
            <NavItem
                v-for="destination in NAV_DESTINATIONS"
                :key="destination.id"
                :destination="destination"
                :layout="layout"
                :selected="activeSection === destination.id"
                :tooltips-suppressed="isSliding"
                @click="onItemClick(destination, $event)"
            />
            <NavItem
                v-if="showPremium"
                :destination="PREMIUM_DESTINATION"
                :layout="layout"
                :selected="activeSection === PREMIUM_DESTINATION.id"
                :tooltips-suppressed="isSliding"
                premium
                @click="onItemClick(PREMIUM_DESTINATION, $event)"
            />
        </div>
    </nav>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// The rail mark is brand, not a nav glyph — it must read as visibly bigger than the
// menu icons below it, and its box matches the 56px rail row chips (Faisal, 2026-09-16).
$mark-box: 56px;
$mark-glyph: 40px;

.nav-sidebar {
    display: flex;
    flex-direction: column;
    padding: 0 $space-8 $space-24;
    color: $c-text-primary;
    background: $c-surface-chrome;

    // Tooltips hang outside the column; clip ONLY while sliding.
    &.nav-sidebar-sliding {
        overflow: hidden;
    }
}

.nav-sidebar-header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    height: $size-top-bar-height;
    padding: 0 $space-12;
}

.nav-sidebar-rail .nav-sidebar-header,
.nav-sidebar-tablet .nav-sidebar-header {
    justify-content: center;
    padding: 0;
}

// The wordmark only renders in `expanded`, so this animates the fade-IN after a rail →
// expanded switch; on collapse it leaves via v-if on the same tick (no fade-out).
.nav-sidebar-brand {
    display: flex;

    @include transition(opacity, fast);
}

.nav-sidebar-sliding .nav-sidebar-brand {
    opacity: 0;
}

// The rail mark: FRISKY mark at rest, crossfades to the expand arrow on hover/focus.
.nav-sidebar-mark {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: $c-text-primary;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: $radius-8;

    @include square($mark-box);
    @include focusRing;
}

.nav-sidebar-mark-glyph,
.nav-sidebar-expand-glyph {
    position: absolute;

    @include transition(opacity);
}

.nav-sidebar-mark-glyph {
    @include square($mark-glyph);
}

.nav-sidebar-expand-glyph {
    color: $c-text-tertiary;
    opacity: 0;
}

.nav-sidebar-mark-control {
    &:hover,
    &:focus-visible {
        .nav-sidebar-mark-glyph {
            opacity: 0;
        }

        .nav-sidebar-expand-glyph {
            color: $c-text-secondary;
            opacity: 1;
        }
    }
}

// `[class]` recipe: BaseButton tertiary is 0,2,0 resting and 0,4,0 on hover.
.nav-sidebar-control[class] {
    color: $c-text-tertiary;

    &:not(:disabled):hover {
        color: $c-text-secondary;
    }
}

.nav-sidebar-rows {
    display: flex;
    flex-direction: column;
    gap: $space-4;
    padding-top: $space-8;
}

// Labels don't render in the rail, so this animates the fade-IN after a rail → expanded
// switch; on collapse they leave via v-if on the same tick (no fade-out).
.nav-sidebar-expanded :deep(.nav-item-label) {
    @include transition(opacity, fast);
}

.nav-sidebar-sliding :deep(.nav-item-label) {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .nav-sidebar-sliding :deep(.nav-item-label),
    .nav-sidebar-sliding .nav-sidebar-brand {
        opacity: 1;
    }
}
</style>
